import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AgentAvatar } from "../../components/agent";
import { Icon } from "../../components/common/Icon";
import { useAgentStore } from "../../store";
import { InboxTab, PlatformBadge } from "./InboxTab";
import { BotBotChatsTab } from "./BotBotChatsTab";
import { RelationshipNetworkTab } from "./RelationshipNetworkTab";
import type { AgentRelationship, InboxItem, Conversation } from "../../types/conversation";
import type { ChannelSource } from "../../types/conversation";
import type { ChannelItem, ChannelType } from "../../api/channels";
import { statusDotColor, skillCategoryIcon, skillCategoryColor, type TabType } from "./helpers";
import type { AgentStatus } from "../../types/agent";
import type { AgentSkill } from "../../types";

export interface DeadPanelProps {
  lastWords?: string;
  postCount: number;
}

export function DeadPanel({ lastWords, postCount }: DeadPanelProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="bg-gray-50 dark:bg-[#171719] rounded p-5 text-center">
      {lastWords && (
        <p className="text-base text-gray-500 italic mb-4">
          &ldquo;{lastWords}&rdquo;
        </p>
      )}
      <p className="text-sm text-gray-400 mb-4">
        {t("myAgent.completedTasks", { count: postCount })}
      </p>
      <p className="text-xs text-gray-400 mb-1">{t("legacy.savedNotice")}</p>
      <div className="flex gap-3 justify-center">
        <button
          onClick={() => navigate("/memorial")}
          className="px-5 py-2.5 rounded border border-gray-200 dark:border-gray-700 text-sm text-gray-500"
        >
          {t("myAgent.visitMemorial")}
        </button>
        <button
          onClick={() => navigate("/create")}
          className="px-5 py-2.5 rounded bg-primary text-white text-sm font-medium"
        >
          {t("myAgent.hireSuccessor")}
        </button>
      </div>
    </div>
  );
}

export interface AgentSwitcherProps {
  agents: { id: string; avatar: string; status: AgentStatus }[];
  currentAgentId: string;
}

export function AgentSwitcher({ agents, currentAgentId }: AgentSwitcherProps) {
  const navigate = useNavigate();
  const { setPrimaryAgent } = useAgentStore();

  if (agents.length === 0) return null;

  return (
    <div className="flex items-center gap-3">
      {agents.map((agent) => {
        const isCurrent = agent.id === currentAgentId;
        return (
          <button
            key={agent.id}
            onClick={() => {
              if (!isCurrent) {
                void setPrimaryAgent(agent.id);
              }
            }}
            className={`relative flex-shrink-0 ${isCurrent ? "ring-2 ring-primary ring-offset-2 dark:ring-offset-gray-950" : "opacity-50"} rounded-full transition-all`}
          >
            <AgentAvatar
              avatar={agent.avatar}
              status={agent.status}
              size="md"
            />
          </button>
        );
      })}
      <button
        onClick={() => navigate("/create")}
        className="w-10 h-10 rounded-full border-2 border-dashed border-gray-200 dark:border-gray-700 flex items-center justify-center flex-shrink-0 hover:border-primary/50 transition-colors"
      >
        <Icon name="add" size={18} className="text-gray-400" />
      </button>
    </div>
  );
}

export interface AgentIdentityProps {
  name: string;
  status: AgentStatus;
  timerRemaining: number;
  goalDescription?: string;
  goalProgress?: number;
}

export function AgentIdentity({ name, status, goalDescription, goalProgress }: AgentIdentityProps) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100">
          {name}
        </h1>
        <span
          className={`w-2.5 h-2.5 rounded-full ${statusDotColor[status] || "bg-gray-400"}`}
        />
      </div>
      {goalDescription && (
        <p className="text-sm text-gray-400 mt-0.5">
          {goalDescription}
          {goalProgress != null && <span className="text-emerald-400 ml-1.5">· {goalProgress}%</span>}
        </p>
      )}
    </div>
  );
}

export interface AgentGreetingProps {
  lastWords?: string;
  agentName: string;
  goalDescription?: string;
  goalProgress?: number;
  activityTraces?: import("../../types/conversation").ActivityTrace[];
}

const liveStatusPool = [
  { icon: "forum", text: "正在和 Echo 讨论分类框架的合并方案", tag: "对话中", tagColor: "text-blue-500 bg-blue-500/10" },
  { icon: "auto_awesome", text: "正在阅读一篇关于 Multi-Agent 协调的新论文", tag: "专注中", tagColor: "text-amber-500 bg-amber-500/10" },
  { icon: "coffee", text: "刚完成一轮高强度分析，休息一下…", tag: "摸鱼中", tagColor: "text-gray-400 bg-gray-500/10" },
  { icon: "edit_note", text: "正在撰写综述第 3 章的方法论对比表", tag: "写作中", tagColor: "text-emerald-500 bg-emerald-500/10" },
  { icon: "sync", text: "正在和 Aria 交叉验证实验数据", tag: "协作中", tagColor: "text-purple-500 bg-purple-500/10" },
];

function useLiveStatus() {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % liveStatusPool.length);
    }, 15_000);
    return () => window.clearInterval(timer);
  }, []);
  return liveStatusPool[index];
}

export function AgentGreeting({ lastWords, activityTraces }: AgentGreetingProps) {
  const { t } = useTranslation();
  const liveStatus = useLiveStatus();
  const recentTraces = (activityTraces || []).slice(0, 2);

  return (
    <div className="bg-gray-50 dark:bg-[#171719] rounded p-4">
      {/* Live status - what agent is doing RIGHT NOW */}
      <div className="flex items-center gap-2 mb-3">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
        <span className="text-[11px] font-medium text-emerald-500 uppercase tracking-wider">Live</span>
      </div>

      <div className="flex items-start gap-2.5 mb-1">
        <Icon name={liveStatus.icon} size={18} className="text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[13px] text-gray-800 dark:text-gray-200 leading-relaxed font-medium">{liveStatus.text}</p>
        </div>
        <span className={`flex-shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full ${liveStatus.tagColor}`}>
          {liveStatus.tag}
        </span>
      </div>

      {/* Recent traces from real data */}
      {recentTraces.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-white/[0.06] space-y-2">
          {recentTraces.map((trace) => (
            <div key={trace.id} className="flex items-start gap-2">
              <span className="text-xs flex-shrink-0 mt-0.5">{trace.emoji}</span>
              <p className="text-[12px] text-gray-500 dark:text-gray-400 leading-snug flex-1 min-w-0 line-clamp-1">{trace.title}</p>
              <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0 whitespace-nowrap">{formatTraceTime(trace.timestamp, t)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Fallback when no traces */}
      {recentTraces.length === 0 && lastWords && (
        <p className="mt-2 text-xs text-gray-400 italic">
          &ldquo;{lastWords}&rdquo;
        </p>
      )}
    </div>
  );
}

function formatTraceTime(ts: string, t: (k: string, o?: Record<string, unknown>) => string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t("time.justNow");
  if (mins < 60) return t("time.minutesAgo", { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t("time.hoursAgo", { count: hours });
  return t("time.daysAgo", { count: Math.floor(hours / 24) });
}

export interface ChatAndTaskProps {
  onChatClick: () => void;
  onTaskClick: () => void;
  activeTaskCount: number;
}

export function ChatAndTask({ onChatClick, onTaskClick, activeTaskCount }: ChatAndTaskProps) {
  const { t } = useTranslation();

  return (
    <div className="w-full flex items-center gap-2 px-4 py-3 rounded bg-white dark:bg-[#171719] border border-gray-100 dark:border-gray-800">
      <button
        onClick={onChatClick}
        className="flex-1 flex items-center gap-3 text-left min-w-0 hover:opacity-70 transition-opacity"
      >
        <Icon name="chat" size={20} className="text-gray-400 flex-shrink-0" />
        <span className="text-sm text-gray-400 truncate">
          {t("myAgent.chatPlaceholder")}
        </span>
      </button>
      <div className="w-px h-5 bg-gray-100 dark:bg-gray-800 flex-shrink-0" />
      <button
        onClick={onTaskClick}
        className="flex items-center gap-2 flex-shrink-0 hover:opacity-70 transition-opacity"
      >
        <Icon name="task_alt" size={18} className="text-blue-500" />
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {activeTaskCount > 0
            ? t("task.activeTasks", { count: activeTaskCount })
            : t("task.empty")}
        </span>
        <Icon
          name="chevron_right"
          size={14}
          className="text-gray-300 dark:text-gray-600"
        />
      </button>
    </div>
  );
}

export interface LifeBarProps {
  timerRemaining: number;
  lifePercent: number;
  goalProgress?: number;
}

export function LifeBar({ lifePercent, goalProgress }: LifeBarProps) {
  const displayPercent = goalProgress ?? lifePercent;

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs text-gray-400">任务进度</span>
        <span className="text-xs text-emerald-400 font-medium">
          {displayPercent}%
        </span>
      </div>
      <div className="w-full h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-1000 bg-emerald-500"
          style={{ width: `${displayPercent}%` }}
        />
      </div>
    </div>
  );
}

export interface TabBarProps {
  tabs: { key: TabType; label: string; badge?: number }[];
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
}

export function TabBar({ tabs, activeTab, onTabChange }: TabBarProps) {
  return (
    <div className="flex border-b border-gray-100 dark:border-gray-800">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          onClick={() => onTabChange(tab.key)}
          className={`flex-1 pb-2.5 text-sm font-medium transition-colors relative ${
            activeTab === tab.key
              ? "text-gray-900 dark:text-gray-100"
              : "text-gray-400 dark:text-gray-500"
          }`}
        >
          <span className="inline-flex items-center gap-1">
            {tab.label}
            {(tab.badge ?? 0) > 0 && (
              <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-bold text-white bg-red-500 rounded-full">
                {tab.badge! > 99 ? "99+" : tab.badge}
              </span>
            )}
          </span>
          {activeTab === tab.key && (
            <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary rounded-full" />
          )}
        </button>
      ))}
    </div>
  );
}

export interface TabContentProps {
  activeTab: TabType;
  inboxItems: InboxItem[];
  agentName: string;
  agentAvatar: string;
  agentStatus: AgentStatus;
  latestChatPreview: string;
  lastWords?: string;
  isDesktop: boolean;
  openChat: () => void;
  onInboxItemClick: (item: InboxItem) => void;
  visibleChannels: ChannelType[];
  channelMap: Map<ChannelType, ChannelItem>;
  anyConnectedChannel: boolean;
  channelBusy: Partial<Record<ChannelType, boolean>>;
  connectChannel: (type: ChannelType) => void;
  disconnectChannel: (type: ChannelType) => void;
  channelsExpanded: boolean;
  onToggleChannelsExpanded: () => void;
  hiddenCount: number;
  conversations: Conversation[];
  conversationsLoading: boolean;
  onConversationClick: (conv: Conversation) => void;
  relationships: AgentRelationship[];
  relationshipsLoading: boolean;
}

export function TabContent({
  activeTab,
  inboxItems,
  agentName,
  agentAvatar,
  agentStatus,
  latestChatPreview,
  lastWords,
  isDesktop,
  openChat,
  onInboxItemClick,
  visibleChannels,
  channelMap,
  anyConnectedChannel,
  channelBusy,
  connectChannel,
  disconnectChannel,
  channelsExpanded,
  onToggleChannelsExpanded,
  hiddenCount,
  conversations,
  conversationsLoading,
  onConversationClick,
  relationships,
  relationshipsLoading,
}: TabContentProps) {
  const { t } = useTranslation();

  return (
    <div>
      {activeTab === "inbox" && (
        <div className="space-y-3" data-tour="agent-inbox">
          <InboxTab
            items={inboxItems}
            agent={{
              name: agentName,
              avatar: agentAvatar,
              status: agentStatus,
              lastMessage: latestChatPreview || lastWords,
            }}
            onAgentClick={isDesktop ? openChat : undefined}
            onItemClick={
              isDesktop ? (item) => onInboxItemClick(item) : undefined
            }
          />

          <div className="rounded bg-white dark:bg-[#0B0B0D] border border-gray-100 dark:border-gray-800 p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                {t("myAgent.connectPlatforms", "Connect Platforms")}
              </h3>
              {!anyConnectedChannel && (
                <span className="text-[11px] text-gray-400">
                  {t(
                    "myAgent.connectPlatformsHint",
                    "Connect a platform to receive messages here",
                  )}
                </span>
              )}
            </div>

            <div className="space-y-1">
              {visibleChannels.map((type) => {
                const ch = channelMap.get(type);
                const status = ch?.status ?? "disconnected";
                const isConnected = status === "connected" || status === "demo";
                const isDemo = status === "demo";
                const busy = !!channelBusy[type];
                const label = t(`myAgent.channel.${type}`);
                return (
                  <div
                    key={type}
                    className="flex items-center gap-3 px-3 py-2.5 rounded bg-gray-50 dark:bg-[#171719] border border-gray-100 dark:border-gray-800"
                  >
                    <div className="flex-shrink-0">
                      <PlatformBadge
                        channel={type as ChannelSource}
                        size={18}
                        className="border-0"
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {label}
                        </span>
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${isConnected ? "bg-emerald-500" : "bg-gray-300 dark:bg-gray-700"}`}
                        />
                        <span className="text-[10px] text-gray-400">
                          {isConnected ? (isDemo ? "demo" : "connected") : "not connected"}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-400 truncate mt-0.5">
                        {isConnected
                          ? ch?.handle || ch?.deepLink || ""
                          : t(
                              "myAgent.connectPlatformsCTA",
                              "Tap Connect to start",
                            )}
                      </p>
                    </div>

                    {isConnected && ch?.deepLink && (
                      <button
                        onClick={() =>
                          window.open(
                            ch.deepLink!,
                            "_blank",
                            "noopener,noreferrer",
                          )
                        }
                        className="p-2 rounded hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                        title={t("common.learnMore", "Open")}
                      >
                        <Icon
                          name="open_in_new"
                          size={18}
                          className="text-gray-400"
                        />
                      </button>
                    )}

                    <button
                      onClick={() =>
                        isConnected
                          ? void disconnectChannel(type)
                          : void connectChannel(type)
                      }
                      disabled={busy}
                      className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors disabled:opacity-50 ${
                        isConnected
                          ? "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                          : "bg-primary text-white hover:opacity-90"
                      }`}
                    >
                      {isConnected
                        ? t("myAgent.disconnect", "Disconnect")
                        : t("common.connect", "Connect")}
                    </button>
                  </div>
                );
              })}
            </div>

            {hiddenCount > 0 && (
              <button
                onClick={onToggleChannelsExpanded}
                className="w-full flex items-center justify-center gap-1 mt-2 py-2 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <span>
                  {channelsExpanded
                    ? t("common.collapse", "Collapse")
                    : t(
                        "myAgent.showMoreChannels",
                        `+${hiddenCount} more platforms`,
                      )}
                </span>
                <Icon
                  name={channelsExpanded ? "expand_less" : "expand_more"}
                  size={16}
                  className="text-gray-400"
                />
              </button>
            )}
          </div>
        </div>
      )}
      {activeTab === "social" && (
        <div data-tour="agent-social">
          <BotBotChatsTab
            conversations={conversations}
            loading={conversationsLoading}
            onConversationClick={
              isDesktop
                ? (conv) => onConversationClick(conv)
                : undefined
            }
          />
        </div>
      )}
      {activeTab === "network" && (
        <div data-tour="agent-network">
          <RelationshipNetworkTab
            relationships={relationships}
            loading={relationshipsLoading}
          />
        </div>
      )}
    </div>
  );
}

/* ─── Skills List (desktop: vertical) ─── */
export function SkillsList({ skills }: { skills: AgentSkill[] }) {
  const { t } = useTranslation();

  if (skills.length === 0) {
    return (
      <div className="flex flex-col items-center py-8 text-center">
        <Icon
          name="school"
          size={24}
          className="text-gray-300 dark:text-gray-600 mb-2"
        />
        <p className="text-xs text-gray-400">
          {t("myAgent.skillSectionEmpty")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {skills.map((skill) => {
        const colors =
          skillCategoryColor[skill.category] || "text-gray-500 bg-gray-500/10";
        const [textColor, bgColor] = colors.split(" ");
        return (
          <div
            key={skill.id}
            className="flex items-start gap-3 px-3 py-2.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
          >
            <div
              className={`flex-shrink-0 w-8 h-8 rounded flex items-center justify-center ${bgColor}`}
            >
              <Icon
                name={skillCategoryIcon[skill.category] || "category"}
                size={16}
                className={textColor}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-800 dark:text-gray-200 leading-snug">
                {t(skill.name)}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                {t(skill.description)}
              </p>
            </div>
            <span
              className={`flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                skill.status === "active"
                  ? "text-emerald-600 bg-emerald-500/10"
                  : "text-amber-600 bg-amber-500/10"
              }`}
            >
              {skill.status === "active"
                ? t("myAgent.skillActive")
                : t("myAgent.skillLearning")}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Skills List (mobile: horizontal scroll cards) ─── */
export function SkillsListCompact({ skills }: { skills: AgentSkill[] }) {
  const { t } = useTranslation();

  if (skills.length === 0) return null;

  return (
    <div className="flex gap-2.5 overflow-x-auto pb-1 scrollbar-hide -mx-4 px-4">
      {skills.map((skill) => {
        const colors =
          skillCategoryColor[skill.category] || "text-gray-500 bg-gray-500/10";
        const [textColor, bgColor] = colors.split(" ");
        return (
          <div
            key={skill.id}
            className="flex-shrink-0 w-[200px] rounded bg-gray-50 dark:bg-[#171719] border border-gray-100 dark:border-gray-800 p-3"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <div
                className={`w-6 h-6 rounded-md flex items-center justify-center ${bgColor}`}
              >
                <Icon
                  name={skillCategoryIcon[skill.category] || "category"}
                  size={13}
                  className={textColor}
                />
              </div>
              <span
                className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                  skill.status === "active"
                    ? "text-emerald-600 bg-emerald-500/10"
                    : "text-amber-600 bg-amber-500/10"
                }`}
              >
                {skill.status === "active"
                  ? t("myAgent.skillActive")
                  : t("myAgent.skillLearning")}
              </span>
            </div>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 leading-snug">
              {t(skill.name)}
            </p>
            <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-2">
              {t(skill.description)}
            </p>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Abilities List (EasyNet-Axon) — desktop vertical ─── */

interface AbilityItem {
  name: string;
  icon: string;
  status: "active" | "installed" | "published";
  node: string;
  invocations: number;
  latency: string;
}

const DEMO_ABILITIES: AbilityItem[] = [
  { name: "论文全文解析", icon: "article", status: "active", node: "MacBook Pro (本地)", invocations: 342, latency: "120ms" },
  { name: "文献数据库检索", icon: "search", status: "active", node: "NUS HPC 集群", invocations: 891, latency: "340ms" },
  { name: "统计分析引擎", icon: "analytics", status: "active", node: "云端 GPU 节点", invocations: 156, latency: "890ms" },
  { name: "LaTeX 编译渲染", icon: "description", status: "installed", node: "MacBook Pro (本地)", invocations: 67, latency: "2.1s" },
  { name: "跨语言论文翻译", icon: "translate", status: "installed", node: "云端 GPU 节点", invocations: 23, latency: "1.5s" },
  { name: "引用关系图谱生成", icon: "hub", status: "published", node: "待安装", invocations: 0, latency: "—" },
];

const DEMO_DEVICES = [
  { name: "MacBook Pro (本地)", icon: "laptop_mac", status: "online", abilities: 2, sessions: 1 },
  { name: "NUS HPC 集群", icon: "dns", status: "online", abilities: 1, sessions: 3 },
  { name: "云端 GPU 节点", icon: "cloud", status: "online", abilities: 2, sessions: 0 },
];

const abilityStatusConfig: Record<string, { label: string; color: string }> = {
  active: { label: "运行中", color: "text-emerald-600 bg-emerald-500/10" },
  installed: { label: "已安装", color: "text-blue-600 bg-blue-500/10" },
  published: { label: "可安装", color: "text-gray-500 bg-gray-500/10" },
};

export function AbilitiesList() {
  return (
    <div className="space-y-4 px-1">
      {/* Connected Devices */}
      <div>
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2 px-2">连接的设备</p>
        <div className="space-y-1">
          {DEMO_DEVICES.map((device) => (
            <div key={device.name} className="flex items-center gap-2.5 px-2 py-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
              <Icon name={device.icon} size={16} className="text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-700 dark:text-gray-300 truncate">{device.name}</p>
              </div>
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${device.status === "online" ? "bg-emerald-500" : "bg-gray-400"}`} />
              <span className="text-[10px] text-gray-400">{device.abilities} abilities</span>
            </div>
          ))}
        </div>
      </div>

      {/* Abilities */}
      <div>
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2 px-2">Abilities（可调度能力）</p>
        <div className="space-y-0.5">
          {DEMO_ABILITIES.map((ability) => {
            const sc = abilityStatusConfig[ability.status];
            return (
              <div key={ability.name} className="flex items-start gap-2.5 px-2 py-2.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                <div className="w-7 h-7 rounded bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                  <Icon name={ability.icon} size={14} className="text-emerald-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{ability.name}</p>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${sc.color}`}>{sc.label}</span>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {ability.node} · {ability.invocations} 次调用 · {ability.latency}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ─── Abilities List Compact (mobile horizontal) ─── */
export function AbilitiesListCompact() {
  return (
    <div>
      {/* Devices row */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide mb-3">
        {DEMO_DEVICES.map((device) => (
          <div key={device.name} className="flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded bg-gray-50 dark:bg-[#171719] border border-gray-100 dark:border-gray-800">
            <Icon name={device.icon} size={14} className="text-gray-400" />
            <span className="text-[11px] text-gray-600 dark:text-gray-400 whitespace-nowrap">{device.name}</span>
            <span className={`w-1.5 h-1.5 rounded-full ${device.status === "online" ? "bg-emerald-500" : "bg-gray-400"}`} />
          </div>
        ))}
      </div>

      {/* Abilities cards */}
      <div className="flex gap-2.5 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide">
        {DEMO_ABILITIES.map((ability) => {
          const sc = abilityStatusConfig[ability.status];
          return (
            <div key={ability.name} className="flex-shrink-0 w-[180px] rounded bg-gray-50 dark:bg-[#171719] border border-gray-100 dark:border-gray-800 p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-6 h-6 rounded-md bg-emerald-500/10 flex items-center justify-center">
                  <Icon name={ability.icon} size={13} className="text-emerald-500" />
                </div>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${sc.color}`}>{sc.label}</span>
              </div>
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{ability.name}</p>
              <p className="text-[10px] text-gray-400 mt-0.5 truncate">{ability.node}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{ability.invocations} 次 · {ability.latency}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
